import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useReportContext } from "../../../../../ReportContext/useReportContext";
import { useTranslation } from "react-i18next";
import { EGInput } from "../../../../../../MainComponents/Input/EGInput";
import { EGButton } from "../../../../../../MainComponents/Button/EGButton/EGButton";
import { EGIconButton } from "../../../../../../MainComponents/Button/IconButton/IconButton";
import { useSnackbar } from "notistack";
import { updateMeasure } from "../../../../../../../apis";
import { useReportId } from "../../../../../../../context/report/ReportContext";
import { CatalogueMultiSelect } from "../../../../../../MainComponents/Catalogues/CatalogueMultiSelect";
import { useActionsCatalogue } from "../../../../../../../hooks/useActionsCatalogue";
import {
  ACTION_TYPES,
  getActionPath,
  getSavedProposalStates,
  normalizeResolutionMeasure,
  splitByOrigin,
  splitProposals,
} from "../../../../../../../helpers/correctiveActions";
import { InvestigationProposals } from "../InvestigationProposals/InvestigationProposals";

export const EditMeasuresForm = ({
  content,
  hdlCloseDialog,
  getAllMeasures,
  reportRender,
  investigationList = [],
}) => {
  // Custom Hooks
  const { measureOptions } = useReportContext();
  const [t] = useTranslation("report");
  const id = useReportId();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const { catalogue, isOwn, rootPath, isLoading: isCatalogueLoading } =
    useActionsCatalogue(ACTION_TYPES.MEASURE);

  const initial = normalizeResolutionMeasure(content);

  // El registro guardado mezcla lo que salio de una investigacion con lo que
  // capturo el resolutor. Se separan para editar cada cosa donde corresponde:
  // las propuestas en su bloque y lo propio en el selector de catalogo.
  const savedStates = getSavedProposalStates({
    actions: initial.actions,
    another: initial.another,
    discarded: content.discarded,
  });

  // Initial States
  const [actions, setActions] = useState(
    () => splitByOrigin(initial.actions).own
  );
  const [another, setAnother] = useState(() =>
    splitByOrigin(initial.another)
      .own.map((item) => item.text)
      .filter(Boolean)
  );
  const [proposals, setProposals] = useState([]);
  const [triedToSave, setTriedToSave] = useState(false);
  const [comment, setComment] = useState(content.comment || "");
  const [addDetails, setAddDetails] = useState(!!content.comment);
  const [isLoading, setIsLoading] = useState(false);

  // Las opciones planas de respaldo excluyen "another": el texto libre tiene su
  // propio campo dentro del selector. Si la medida guardada no esta en la lista
  // (registro previo al catalogo), se agrega para que siga visible.
  const fallbackOptions = (() => {
    const base = measureOptions.filter((option) => option.value !== "another");
    const missing = actions
      .map(getActionPath)
      .filter((path) => path && !base.some((option) => option.value === path))
      .map((path) => ({ value: path, label: path }));
    return [...base, ...missing];
  })();

  // Lo que se toma de la investigacion y lo que el resolutor decidio no aplicar
  const { confirmed, another: proposalAnother, discarded } =
    splitProposals(proposals);

  // Un descarte sin motivo deja el registro sin explicar por que no se aplico.
  const hasMissingReason = discarded.some((item) => !item.reason);

  // Validation info
  const isInvalid =
    (confirmed.length === 0 &&
      proposalAnother.length === 0 &&
      actions.length === 0 &&
      another.filter((text) => text.trim() !== "").length === 0) ||
    another.some((text) => text.trim() !== "" && text.trim().length < 3) ||
    hasMissingReason;

  // Sending form
  const onSubmitMesure = async (e) => {
    e.preventDefault();
    setTriedToSave(true);
    if (hasMissingReason) {
      enqueueSnackbar(t("Resolution.discard_reason_required"), {
        variant: "error",
      });
      return;
    }

    const measureDataUpdate = {
      report_identifier: Number(id),
      id: content.id,
      type: ACTION_TYPES.MEASURE,
      measures: [...confirmed, ...actions],
      another: [
        ...proposalAnother,
        ...another
          .map((text) => text.trim())
          .filter(Boolean)
          .map((text) => ({ text })),
      ],
      discarded,
      comment,
    };
    setIsLoading(true);

    const sending = enqueueSnackbar(`${t("Resolution.sending")}...`, {
      variant: "info",
      persist: true,
    });

    try {
      await updateMeasure(measureDataUpdate).then((resp) => {
        if (resp.status === 201) {
          setIsLoading(false);
          hdlCloseDialog();
          getAllMeasures();
          reportRender();
          closeSnackbar(sending);
          enqueueSnackbar(t("Resolution.measure_added_successfully"), {
            variant: "success",
          });
        }
      });
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      closeSnackbar(sending);
    }
  };

  return (
    <>
      <EGIconButton
        iconName="cancel_circle"
        iconColor="primary"
        onClick={hdlCloseDialog}
        sx={{ position: "absolute", top: 15, right: 12 }}
        iconSize={25}
      />
      <DialogTitle align="center" component="h3" color="secondary">
        {t("Resolution.corrective_preventive_measure")}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {/* Mismo orden que el alta: propuestas de las investigaciones y al
            final lo que agrega el resolutor por su cuenta. */}
        <InvestigationProposals
          investigations={investigationList}
          type={ACTION_TYPES.MEASURE}
          proposals={proposals}
          onProposalsChange={setProposals}
          showErrors={triedToSave}
          savedStates={savedStates}
        />

        <Typography
          variant="body2"
          fontWeight={600}
          color="secondary"
          component="div"
          mt={2}
          mb={1}
        >
          {t("Resolution.add_on_your_own")}
        </Typography>

        {!isCatalogueLoading && (
          <CatalogueMultiSelect
            label={`${t("Resolution.select_corrective_preventive_measure")}:`}
            placeholder={t("Resolution.select_corrective_preventive_measure")}
            catalogue={catalogue}
            isOwn={isOwn}
            rootPath={rootPath}
            options={fallbackOptions}
            value={actions}
            onChange={setActions}
            another={another}
            onAnotherChange={setAnother}
            freeTextLabel={t("Resolution.new_corrective_preventive_measure")}
            freeTextPlaceholder={t(
              "Resolution.example_implement_monthly_audits"
            )}
            addFreeTextTitle={t(
              "Resolution.enter_new_corrective_preventive_measure"
            )}
          />
        )}

        {!addDetails && (
          <Typography textAlign="end" my={0.33}>
            <Link
              underline="hover"
              onClick={() => {
                setAddDetails(true);
                setTimeout(() => {
                  window.scrollTo({
                    top: document.documentElement.scrollHeight,
                    behavior: "smooth",
                  });
                }, 100);
              }}
            >
              {`+ ${t("Resolution.add_comment")}`}
            </Link>
          </Typography>
        )}

        {addDetails && (
          <EGInput
            sx={{ mt: 1.75, mb: 0.75 }}
            label={`${t("Resolution.details")}: ${comment.length}/500`}
            placeholder={t("Resolution.enter_details_related_selected_measure")}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            multiline
            inputProps={{ maxLength: 500 }}
            rows={3.5}
            autoFocus
          />
        )}
      </DialogContent>
      <DialogActions>
        <EGButton
          label={t("Resolution.save")}
          onClick={onSubmitMesure}
          loading={isLoading}
          iconName={isInvalid ? "mark_check" : "mark_check_bold"}
          disabled={isInvalid}
          color={isInvalid ? "light" : "success"}
          iconColor={isInvalid ? "" : "white"}
          iconColorHover={isInvalid ? "" : "white"}
          labelColor={isInvalid ? "" : "white"}
        />
        <EGButton
          iconName="close_icon"
          iconSize={21}
          label={t("Resolution.cancel")}
          onClick={hdlCloseDialog}
          loading={isLoading}
        />
      </DialogActions>
    </>
  );
};
